import React, { useEffect, useState } from "react";
import { Modal, Typography, List, Button, Space } from "antd";
import { useInstall } from "./InstallProvider";

const { Title, Paragraph, Text } = Typography;

const STORAGE_KEY = "update_notice_v2_0_dismissed";
const CUTOFF_MS = Date.UTC(2026, 0, 1); // Jan 1, 2026 00:00:00 UTC

function UpdateMessageModal({
  otherInfo,
}) {
  const [open, setOpen] = useState(false);
  const { isStandalone, promptInstall } = useInstall();

  // Install state handled by provider

  const handleInstallClick = async () => {
    if (isStandalone) return;
    const result = await promptInstall();
    if (!result || result.status === "unavailable" || result.status === "error") {
      window.alert(
        "Install not available or the app is already installed.\n\nSafari (macOS): Safari → Add to Dock.\nChrome: Address bar ‘Install’ icon or 3‑dots → Save and install.\nEdge: 3‑dots → Apps → Install this site as an app."
      );
    }
  };

  useEffect(() => {
    // Only decide once otherInfo is available
    if (!otherInfo) return;
    const normalizeIsoString = (s) => {
      if (typeof s !== "string") return s;
      // Trim fractional seconds to 3 digits (JS Date supports ms)
      // e.g. "2025-12-19T14:21:36.138495+00:00" -> "2025-12-19T14:21:36.138+00:00"
      return s.replace(/(\.\d{3})\d+/, "$1");
    };
    const parseToMs = (ts) => {
      if (ts === null || ts === undefined) return null;
      if (typeof ts === "number") {
        // seconds vs ms
        return ts < 1e12 ? ts * 1000 : ts;
      }
      if (typeof ts === "string") {
        const s = normalizeIsoString(ts);
        const num = Number(s);
        if (!Number.isNaN(num)) return num < 1e12 ? num * 1000 : num;
        // Try robust datetime parsing:
        // 1) Native parse
        // 2) Replace space with 'T'
        // 3) If no timezone provided, assume UTC ('Z')
        const attempts = [s, s.replace(" ", "T")];
        const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(s);
        if (!hasTz) {
          attempts.push(s.replace(" ", "T") + "Z");
        }
        for (const candidate of attempts) {
          const d = Date.parse(candidate);
          if (!Number.isNaN(d)) return d;
        }
        return null;
      }
      if (ts instanceof Date) return ts.getTime();
      return null;
    };

    // Support multiple possible prop names
    const rawTs =
      otherInfo.last_login ??
      otherInfo.last_login_ts ??
      otherInfo.lastLogin ??
      otherInfo.lastLoginTs ??
      null;
    const lastLoginMs = parseToMs(rawTs);
    if (lastLoginMs === null) return; // don't show if unknown

    const isBeforeCutoff = lastLoginMs < CUTOFF_MS;
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(STORAGE_KEY) === "true";
    } catch {
      // ignore storage errors
    }
    if (isBeforeCutoff && !dismissed) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [otherInfo]);

  const handleClose = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "true");
    } catch {
      // ignore storage errors
    }
    setOpen(false);
  };

  return (
    <Modal
      title="v2.0 is live 🚀🔥"
      open={open}
      onOk={handleClose}
      onCancel={handleClose}
      okText="Got it"
      rootClassName="liquid-glass-modal"
      footer={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>

          <Space >
            <Button
              style={{ marginTop: 12 }}
              onClick={() =>
                window.open(
                  "https://www.youtube.com/watch?v=DlS4MOAhLs0",
                  "_blank",
                  "noreferrer"
                )
              }
            >
              Watch demo
            </Button>

            <Button
              style={{ marginTop: 12 }}
              onClick={handleInstallClick}
              disabled={isStandalone}
            >
              {isStandalone ? "Installed" : "Install app"}
            </Button>
            <Button type="primary" className="primary-glass-btn" style={{ marginTop: 12 }} onClick={handleClose}>
              Got it
            </Button>
          </Space>
        </Space>
      }
    >
      <div>
        <Title level={4} style={{ marginTop: 0 }}>
          What’s new?
        </Title>
        <List
          size="small"
          dataSource={[
            "• Brand-new design for a cleaner, faster UI experience ✨ ",
            "• Enhanced workout search with smart filters for duration, category, and TSS 🔍⚡",
            "• Cadence reading improvements (fingers crossed! 🤞) 🔧",
            "• Embed any YouTube video directly into your session for extra motivation 🎥",
            "• Start or join live sessions to train together with friends 🚴‍♂️🚴‍♀️",
            "• New Simpl'AI coach: create a single workout or a full training program tailor‑made for you 🤖🧠",
            "• Install the app on your desktop for a better user experience and to avoid gaps in data points 💻📱",
          ]}
          renderItem={(item) => <List.Item style={{ paddingLeft: 0 }}>{item}</List.Item>}
        />
        <Title level={4} style={{ marginTop: 0 }}>
          Need help ?
        </Title>
        <Paragraph style={{ marginTop: 12 }}>
          Reminder: from the sidebar, you can{" "}
          <Text strong>contact me</Text>, <Text strong>send feedback</Text>, <Text strong>check the FAQ </Text>, and{" "}
          <Text strong>switch back to the latest stable version</Text> if this update
          doesn’t suit you or has issues.
        </Paragraph>
        <Paragraph>
          Bottom‑right, you'll find a <Text strong>demo video 🎬</Text> showing how the
          platform works and if this platform helps you, consider{" "}
          <Text strong>buying me a coffee ☕ 😊 🙏</Text>
        </Paragraph>

      </div>
    </Modal>
  );
}

export default UpdateMessageModal;

